<template>
  <div class="patient-detail-page">
    <!-- 左侧边栏 -->
    <div class="sidebar">
      <div class="sidebar-header">
        <i class="el-icon-s-fold" />
        <span>收起列表</span>
      </div>
      <div class="search-box">
        <el-input
          v-model="searchKeyword"
          placeholder="患者名称"
          prefix-icon="el-icon-search"
          size="small"
          clearable
        />
        <el-button size="small" icon="el-icon-search" circle @click="handleSearch" />
      </div>
      <div class="patient-list">
        <div
          v-for="patient in patientList"
          :key="patient.id"
          class="patient-item"
          :class="{ active: patient.id === currentPatient.id }"
          @click="selectPatient(patient)"
        >
          <img v-if="patient.sexSign !== '1'" src="@/assets/icon/wuman.png" class="patient-avatar" alt="头像">
          <img v-else src="@/assets/icon/man.png" class="patient-avatar" alt="头像">
          <div class="patient-info-text">
            <div class="patient-name">{{ patient.name }}</div>
            <div v-if="patient.code" class="patient-phone">{{ patient.userName }}</div>
          </div>
        </div>
        <!-- 空状态 -->
        <div v-if="patientList.length === 0" class="empty-state">
          <i class="el-icon-search" />
          <span>暂无匹配患者</span>
        </div>
      </div>
      <!-- 加载更多 -->
      <div v-if="hasMoreData" class="load-more-wrapper">
        <el-button
          type="text"
          :loading="loadingMore"
          @click="loadMore"
        >
          点击加载更多
        </el-button>
      </div>
      <div v-else-if="patientList.length > 0" class="no-more-data">
        没有更多了
      </div>
    </div>

    <!-- 右侧主内容区 - 仅当选择患者后显示 -->
    <div v-if="currentPatient && currentPatient.id" class="main-content">
      <!-- 顶部患者信息栏 - 按设计图重构 -->
      <div class="patient-header">
        <!-- 左侧区域：头像 + 基本信息 + 输入框 -->
        <div class="header-left">
          <img
            v-if="currentPatient.gender !== '1'"
            src="@/assets/icon/wuman.png"
            class="header-avatar"
            alt="头像"
          >
          <img
            v-else
            src="@/assets/icon/man.png"
            class="header-avatar"
            alt="头像"
          >

          <div class="header-info">
            <div class="info-row">
              <span class="patient-name">{{ currentPatient.name }}</span>
              <span class="patient-meta">{{ currentPatient.gender !=='1'?'女':'男' }}/{{ currentPatient.age }}岁</span>
              <span class="patient-phone">{{ currentPatient.phone }}</span>
            </div>

            <div class="input-row">
              <div class="input-item">
                <i class="el-icon-s-order input-icon" />
                <el-input
                  v-model="currentPatient.label"
                  placeholder="添加患者标签"
                  size="mini"
                  class="tag-input"
                />
              </div>
              <div class="input-item">
                <i class="el-icon-edit-outline input-icon" />
                <el-input
                  v-model="currentPatient.patientRemark"
                  placeholder="添加备注"
                  size="mini"
                  class="remark-input"
                />
                <el-button type="primary" size="small" @click="saveRL()">保存</el-button>
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧区域：详细信息 -->
        <div class="header-right">
          <div class="info-line">
            <span class="info-item">
              <label>出生日期：</label>
              <value>{{ patientDetail.birthDate || '未知' }}</value>
            </span>
            <span class="divider">|</span>
            <span class="info-item">
              <label>身高：</label>
              <value>{{ patientDetail.height + 'cm' || '未知' }}</value>
            </span>
            <span class="divider">|</span>
            <span class="info-item">
              <label>体重：</label>
              <value>{{ patientDetail.weight + 'kg' || '未知' }}</value>
            </span>
            <span class="divider">|</span>
            <span class="info-item">
              <label>血型：</label>
              <value>{{ patientDetail.bloodType || '未知' }}</value>
            </span>
          </div>

          <div class="info-line">
            <span class="info-item">
              <label>注册日期：</label>
              <value>{{ patientDetail.registerDate || '' }}</value>
            </span>
            <span class="divider">|</span>
            <span class="info-item address-item">
              <label>地址：</label>
              <value>{{ patientDetail.address || '' }}</value>
            </span>
          </div>

          <div class="info-line">
            <span class="info-item disease-item">
              <label>疾病史：</label>
              <value>{{ patientDetail.hisContent || '' }}</value>
            </span>
          </div>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="content-body">
        <HealthDashboard :user-code="currentPatient.phone" :sexsign="currentPatient.gender" />
      </div>
    </div>

    <!-- 右侧空白状态 - 未选择患者时显示 -->
    <div v-else class="main-content empty-content">
      <div class="empty-state-center">
        <i class="el-icon-user" />
        <p>请选择左侧患者查看详情</p>
      </div>
    </div>
  </div>
</template>

<script>
import { findPopup } from "@/api/ik/data/dataApi"
import HealthDashboard from './HealthDashboard.vue'
import store from '@/store'
import { postData } from "@/api/user/authApi"

export default {
  name: 'PatientDetail',
  components: {
    HealthDashboard
  },
  data() {
    return {
      // 搜索关键词
      searchKeyword: '',
      // 分页
      currentPage: 1,
      pageSize: 10,
      total: 0,
      // 当前患者 - 初始为空，不显示右侧内容
      currentPatient: {},
      // 患者列表
      patientList: [],
      // 加载更多状态
      loadingMore: false,

      // 患者表单数据（标签、备注）
      patientForm: {
        tag: '',
        remark: ''
      },

      // 患者详细信息存储对象（预留，后续对接接口）
      patientDetail: {
        birthDate: '', // 出生日期
        height: '', // 身高
        weight: '', // 体重
        bloodType: '', // 血型
        registerDate: '', // 注册日期
        address: '', // 地址
        diseaseHistory: '' // 疾病史
      }
    }
  },
  computed: {
    // 判断是否还有更多数据
    hasMoreData() {
      return this.patientList.length < this.total
    }
  },
  mounted() {
    this.getdiagrecord(1, false)
  },
  methods: {
    // 搜索处理（带防抖）
    handleSearch() {
      this.currentPage = 1
      this.getdiagrecord(1, false)
    },
    // 加载更多
    loadMore() {
      if (this.loadingMore) return
      const nextPage = this.currentPage + 1
      this.searchKeyword = ''
      this.getdiagrecord(nextPage, true)
    },
    calculateAge(birthDateStr) {
      if (!birthDateStr) return null
      const birthDate = new Date(birthDateStr)
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      if (today.getMonth() < birthDate.getMonth() ||
        (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())) {
        age--
      }
      return age < 0 ? 0 : age
    },
    // 选择患者
    selectPatient(patient) {
      // this.currentPatient = {
      //   id: patient.id,
      //   name: patient.name,
      //   gender: patient.sexSign,
      //   phone: patient.code || '',
      //   avatar: patient.avatar,
      //   birthDate: patient.birthDate,
      //   age: this.calculateAge(patient.birthDate)
      // }
      postData('clinic/treatment/patient/patient/find/findpatient', {
        "userCode": patient.userCode,
        "storeCode": store.getters['user/getStoreCode']
      }).then(res => {
        if (res.data) {
          // 如果接口返回详细数据，可以在此处更新 patientDetail
          // this.currentPatient = res.data
          this.currentPatient = {
            id: res.data.id,
            code: res.data.code,
            name: res.data.name,
            gender: res.data.sexSign,
            phone: res.data.userCode || '',
            avatar: res.data.avatar,
            birthDate: res.data.birthDate,
            age: this.calculateAge(res.data.birthDate),
            patientRemark: res.data.patientRemark,
            label: res.data.label
          }
          this.patientDetail.birthDate = res.data.birthDate
          this.patientDetail.bloodType = this.convertBloodType(res.data.bloodTypeSign)
          this.patientDetail.address = res.data.contactAddress
          // 其他字段同理...
        }
      })
      // 重置表单
      this.patientForm = {
        tag: '',
        remark: ''
      }

      // TODO: 后续在此处调用接口获取详细档案信息，填充 patientDetail 对象
      // this.getPatientDetail(patient.code)

      // 重新获取患者健康信息
      this.getHealthInfo(patient.userCode)
    },
    convertBloodType(sign) {
      const map = {
        '1': 'A型',
        '2': 'B型',
        '3': 'AB型',
        '4': 'O型'
      }
      return map[sign] || sign || '未知'
    },
    // 获取患者详细信息（预留方法）
    getPatientDetail(code) {
      // 预留接口调用位置
      // 返回数据应包含：birthDate, height, weight, bloodType, registerDate, address, diseaseHistory
      // 赋值给 this.patientDetail
    },

    getHealthInfo(code) {
      postData('health/health/health/healthinfo/find/findhealthinfo', {
        "userCode": code
      }).then(res => {
        if (res.data) {
          // 如果接口返回详细数据，可以在此处更新 patientDetail
          this.patientDetail.height = res.data.height || ''
          this.patientDetail.weight = res.data.weight || ''
          this.patientDetail.hisContent = res.data.hisContent
          this.patientDetail.registerDate = res.data.entryTime
          // 其他字段同理...
        }
      })
    },

    // 获取患者列表（后端分页）
    getdiagrecord(page, isLoadMore = false) {
      if (isLoadMore) {
        this.loadingMore = true
      }

      const pd = {
        storeCode: store.getters['user/getStoreCode'],
        name: this.searchKeyword,
        userCodeIsNotNull: true
      }
      findPopup("clinic/treatment/patient/patient/find/findPagination", pd, true, this.pageSize, page).then(res => {
        if (res.data && res.data.list) {
          const filteredList = res.data.list
          if (isLoadMore) {
            this.patientList = [...this.patientList, ...filteredList]
          } else {
            this.patientList = filteredList
          }
          this.total = res.data.totalCount || 0
          this.currentPage = page
        } else {
          if (!isLoadMore) {
            this.patientList = []
            this.total = 0
          }
        }
      }).catch(() => {
        if (!isLoadMore) {
          this.patientList = []
          this.total = 0
        }
      }).finally(() => {
        this.loadingMore = false
      })
    },
    saveRL() {
      const pd = {
        code: this.currentPatient.code,
        patientRemark: this.currentPatient.patientRemark,
        label: this.currentPatient.label
      }
      postData('clinic/treatment/patient/patient/do/remarkpatient', pd).then(res => {
        this.$message.success('保存成功')
      })
    }
  }
}
</script>

<style scoped>
.patient-detail-page {
  display: flex;
  height: calc(100vh - 120px);
  background-color: #f5f7fa;
}

/* 左侧边栏 */
.sidebar {
  width: 280px;
  background-color: #fff;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #606266;
  font-size: 14px;
  border-bottom: 1px solid #e4e7ed;
}

.search-box {
  padding: 15px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
}

.search-box >>> .el-input__inner {
  border-radius: 20px;
}

.patient-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.patient-item {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  cursor: pointer;
  transition: all 0.3s;
  margin: 0 8px;
  border-radius: 6px;
}

.patient-item:hover {
  background-color: #f5f7fa;
}

.patient-item.active {
  background-color: #ecf5ff;
}

.patient-item.active .patient-name {
  color: #409eff;
}

.patient-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 12px;
  object-fit: cover;
}

.patient-info-text {
  flex: 1;
  overflow: hidden;
}

.patient-name {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.patient-phone {
  font-size: 12px;
  color: #909399;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #909399;
  font-size: 14px;
}

.empty-state i {
  font-size: 24px;
  margin-bottom: 8px;
}

/* 加载更多按钮 */
.load-more-wrapper {
  padding: 12px;
  border-top: 1px solid #ebeef5;
  text-align: center;
}

.load-more-wrapper >>> .el-button {
  color: #409eff;
  font-size: 13px;
}

/* 没有更多数据提示 */
.no-more-data {
  padding: 12px;
  text-align: center;
  font-size: 12px;
  color: #909399;
  border-top: 1px solid #ebeef5;
}

/* 右侧主内容区 */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 右侧空白状态 */
.empty-content {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
}

.empty-state-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #909399;
}

.empty-state-center i {
  font-size: 64px;
  margin-bottom: 16px;
  color: #dcdfe6;
}

.empty-state-center p {
  font-size: 16px;
  color: #909399;
}

/* ==================== 重构的患者信息头部 ==================== */
.patient-header {
  background-color: #fff;
  padding: 20px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 40px;
  min-height: 120px;
}

/* 左侧区域 */
.header-left {
  display: flex;
  gap: 15px;
  flex: 1;
}

.header-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.info-row .patient-name {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 0;
}

.info-row .patient-meta {
  font-size: 14px;
  color: #606266;
  background-color: #f0f2f5;
  padding: 2px 8px;
  border-radius: 4px;
}

.info-row .patient-phone {
  font-size: 14px;
  color: #409eff;
  font-weight: 500;
}

/* 输入框区域 */
.input-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.input-icon {
  color: #409eff;
  font-size: 14px;
  width: 16px;
}

.tag-input,
.remark-input {
  width: 220px;
}

.tag-input >>> .el-input__inner,
.remark-input >>> .el-input__inner {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #f5f7fa;
}

.tag-input >>> .el-input__inner:focus,
.remark-input >>> .el-input__inner:focus {
  border-color: #409eff;
  background-color: #fff;
}

/* 右侧区域 */
.header-right {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1.5;
  padding-top: 4px;
}

.info-line {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #606266;
}

.info-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.info-item label {
  color: #909399;
  font-weight: normal;
}

.info-item value {
  color: #303133;
  font-weight: 500;
}

.divider {
  color: #dcdfe6;
  font-weight: 300;
}

.address-item,
.disease-item {
  flex: 1;
  min-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 内容区域 */
.content-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

/* 确保 HealthDashboard 在内容区域内正确显示 */
.content-body >>> .health-dashboard {
  background: transparent;
  padding: 0;
  height: auto;
}

/* 响应式适配 */
@media screen and (max-width: 1200px) {
  .patient-header {
    flex-direction: column;
    gap: 20px;
  }

  .header-right {
    padding-left: 75px; /* 与头像对齐 */
    padding-top: 0;
  }

  .address-item,
  .disease-item {
    white-space: normal;
  }
}
</style>
